// CTSK-121 — invoke-action launch parameter form.
//
// Opens ONLY for verb:'invoke' palette actions that declare
// `paramDefaults` in their manifest: the form pre-populates with the
// declared defaults (the whole point of the ticket — no more retyping
// depth/timeout/format on every run) and every value stays editable
// before launch. Actions WITHOUT paramDefaults never see this modal —
// they invoke zero-click exactly as before (backward compat).
//
// Value handling: defaults arrive as JSON values (numbers, booleans,
// strings, nested objects). Each renders as a text input seeded with
// its JSON-ish representation; on submit we JSON.parse when the text
// parses, else pass the raw string. That keeps `depth: 3` a number
// on the wire without forcing operators to quote strings.

import { useEffect, useMemo, useState } from 'react';

export interface ToolActionParamsModalProps {
  /** Friendly action label for the header. */
  actionLabel: string;
  /** The manifest-declared defaults seeding the form. */
  paramDefaults: Record<string, unknown>;
  onRun: (params: Record<string, unknown>) => void;
  onCancel: () => void;
}

/** Seed text for one default value: strings verbatim, everything else
 *  via JSON so numbers/booleans/objects round-trip. */
function seedText(v: unknown): string {
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/** Inverse of seedText: parse JSON when it parses, else raw string. */
function parseText(raw: string): unknown {
  const t = raw.trim();
  if (t === '') return '';
  try {
    return JSON.parse(t);
  } catch {
    return raw;
  }
}

export function ToolActionParamsModal({
  actionLabel,
  paramDefaults,
  onRun,
  onCancel,
}: ToolActionParamsModalProps) {
  const keys = useMemo(() => Object.keys(paramDefaults), [paramDefaults]);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(keys.map((k) => [k, seedText(paramDefaults[k])])),
  );

  // Esc closes — same affordance as the other modals.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  function submit() {
    onRun(Object.fromEntries(keys.map((k) => [k, parseText(values[k] ?? '')])));
  }

  return (
    <div
      role="presentation"
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Run ${actionLabel} — parameters`}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface-raised)',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
          padding: 20,
          width: 380,
          maxWidth: '90vw',
        }}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Run {actionLabel}</h3>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-muted)' }}>
          Pre-filled with this action&apos;s defaults — adjust before running.
        </p>
        <form
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          {keys.map((k) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{ width: 110, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {k}
              </span>
              <input
                type="text"
                value={values[k] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                style={{ flex: 1, padding: 6, border: '1px solid var(--border-strong)', borderRadius: 4, fontFamily: 'var(--font-mono)', fontSize: 12 }}
              />
            </label>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
            <button type="button" className="btn" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn btn--primary" autoFocus>Run</button>
          </div>
        </form>
      </div>
    </div>
  );
}
